import { Denominator, LimitControlEntity, LimitType, Portfolio, SubLimitType } from "../../domain/entities/LimitControlEntity";
import { enrichLimitControl } from "../../infrastructure/LimitControlMapper";
import {
    useGetLimits,
    useGetLimitTypes,
    useGetSubLimitTypes,
    useGetPortfolio as useGetPortfolios,
    useGetDenominator as useGetDenominators,
} from "./useLimitControlQueries";

import { useMemo } from "react";

type OptionItem = { id: number | string; name: string };

const mapToOptions = (data: OptionItem[] = []) => {
    return data.map(({ id, name }) => ({
        label: name,
        value: id.toString(),
    }));
}

export const useLimitConfigData = (limitTypeSelected: string) => {
    const { data: limitsData, isLoading: limitsIsLoading, refetch: limitsRefetch } = useGetLimits();
    const { data: limitTypesData, isLoading: limitTypesIsLoading } = useGetLimitTypes();
    const {
        data: sublimitTypesData,
        isLoading: sublimitTypesIsLoading,
        refetch: sublimitTypesRefetch,
    } = useGetSubLimitTypes();
    const { data: portfoliosData, isLoading: portfoliosIsLoading } = useGetPortfolios();
    const { data: denominatorsData, isLoading: denominatorsIsLoading } = useGetDenominators();

    const limitTypes = useMemo(() => mapToOptions(limitTypesData?.data), [limitTypesData]);
    const subLimitTypes = useMemo(() => mapToOptions(sublimitTypesData?.data), [sublimitTypesData]);
    const subLimitTypesFiltered = useMemo(() => mapToOptions(sublimitTypesData?.data?.filter(st => st.limitTypeId === limitTypeSelected.toString())), [limitTypeSelected]);
    const portfolios = useMemo(() => mapToOptions(portfoliosData?.data), [portfoliosData]);
    const denominators = useMemo(() => mapToOptions(denominatorsData?.data), [denominatorsData]);

    const limits: LimitControlEntity[] = [];
    if (limitsData?.data) {
        limitsData.data.map((item) => {
            const { id, limitEvaluation, percentage, denominator, limitType, portfolio, subLimit, createdAt, updatedAt } = item;

            const enrichedItem = enrichLimitControl(
                {
                    limitEvaluation,
                    percentage,
                    denominator,
                    limitType,
                    portfolio,
                    subLimit,
                },
                id,
                new Date(createdAt),
                updatedAt ? new Date(updatedAt) : null,
                {
                    limitTypes: limitTypesData?.data as unknown as LimitType[],
                    subLimits: sublimitTypesData?.data as unknown as SubLimitType[],
                    denominators: denominatorsData?.data as unknown as Denominator[],
                    portfolios: portfoliosData?.data as unknown as Portfolio[],
                }
            );

            if (enrichedItem) {
                limits.push(enrichedItem);
            }
        });
    }

    const isLoading =
        limitsIsLoading ||
        limitTypesIsLoading ||
        sublimitTypesIsLoading ||
        portfoliosIsLoading ||
        denominatorsIsLoading;

    return {
        limits,
        limitTypes,
        subLimitTypes,
        subLimitTypesFiltered,
        portfolios,
        denominators,
        isLoading,
        limitsRefetch,
        sublimitTypesRefetch,
    };
}
